// utils/globalPass.ts
//
// Global Pass: open ONE other country's collection for 24 hours.
//   Free → 1 pass per month · Pro → 2 passes per month · Global → no pass needed.
//
// The backend (/api/backend/global-pass, helper/globalPass.js) owns every rule and the
// clock. This file only asks the server and shows the answer, so clearing the browser
// or changing the device clock gives nothing.

import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { api } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import i18n from "../i18n";
import { getRegion } from "./regions";
import { regionLabel } from "./regionLabel";

export const PASS_DURATION_MS = 24 * 60 * 60 * 1000;

export type GlobalPassStatus = "ready" | "dismissed" | "active" | "ended" | "used";

/** What GET /status (and every action) returns. */
export interface GlobalPassState {
  tier: "free" | "pro" | "global";
  /** False for Global: the card is never shown. */
  eligible: boolean;
  /** Passes per month for this plan (Free 1, Pro 2). */
  limit: number;
  used: number;
  remaining: number;
  status: GlobalPassStatus;
  /** Country of the latest pass, or null if none this month. */
  region: string | null;
  /** Epoch ms (server time) the latest pass ends. */
  expiresAt: number | null;
  serverNow: number;
}

const BASE = "/backend/global-pass";

/** Message to show when the server refuses (no passes left, bad country, ...). */
export const passErrorMessage = (err: any): string =>
  i18n.t(`globalPass.errors.${err?.response?.data?.code}`, {
    defaultValue: i18n.t("globalPass.errors.default"),
  });

export const useGlobalPass = (userKey: string | undefined) => {
  const [state, setState] = useState<GlobalPassState | null>(null);
  // Server time minus device time, so the countdown follows the server clock.
  const [skew, setSkew] = useState(0);
  const [now, setNow] = useState(Date.now());

  const apply = useCallback((p: GlobalPassState) => {
    setState(p);
    setSkew(p.serverNow - Date.now());
  }, []);

  const refresh = useCallback(
    () =>
      api
        .get(`${BASE}/status`, { skipAuthRedirect: true })
        .then((res) => apply(res.data.pass))
        .catch(() => undefined), // card stays hidden if the server is unreachable
    [apply],
  );

  useEffect(() => {
    if (userKey) refresh();
  }, [userKey, refresh]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const serverNow = now + skew;
  const msLeft = state?.expiresAt ? Math.max(0, state.expiresAt - serverNow) : 0;

  // The pass ran out while the page was open: ask the server what comes next.
  const timedOut = state?.status === "active" && msLeft === 0;
  useEffect(() => {
    if (timedOut) refresh();
  }, [timedOut, refresh]);

  const call = useCallback(
    async (path: string, body?: object) => {
      const res = await api.post(`${BASE}/${path}`, body ?? {}, { skipAuthRedirect: true });
      apply(res.data.pass);
    },
    [apply],
  );

  return {
    state,
    loaded: state !== null,
    msLeft,
    /** Throws when the server refuses; show passErrorMessage(err). */
    activate: (region: string) => call("activate", { region }),
    dismiss: () => call("dismiss").catch(() => undefined),
    markEndedSeen: () => call("ended-seen").catch(() => undefined),
  };
};

// ─── Exploring the pass country ─────────────────────────────────────────────
//
// "Explore" swaps user.region to the pass country on THIS device only, and remembers
// the home country in user.globalPassHomeRegion. The server keeps the real home region
// (it refuses to switch to a country the user doesn't own), so nothing outlives the pass:
// chat to that country is refused by the server the moment the pass ends.

interface PassViewUser {
  region?: string;
  globalPassRegion?: string | null;
  globalPassExpiresAt?: number | null;
  globalPassHomeRegion?: string | null;
}

/** The user's own country, even while they are exploring a pass country. */
export const getHomeRegion = (user: PassViewUser | null | undefined): string =>
  user?.globalPassHomeRegion || user?.region || "healjai";

/** True while the app is showing the pass country instead of the home country. */
export const isExploringPass = (user: PassViewUser | null | undefined): boolean =>
  !!user?.globalPassHomeRegion && !!user.globalPassRegion && user.region === user.globalPassRegion;

export const usePassView = () => {
  const { user, updateCurrentUser } = useAuth();
  const u = user as PassViewUser | null;

  /** Show the pass country across the app (sidebar, Today, chat). */
  const explore = useCallback(() => {
    if (!u?.globalPassRegion || isExploringPass(u)) return;
    updateCurrentUser({ region: u.globalPassRegion, globalPassHomeRegion: getHomeRegion(u) } as any);
  }, [u, updateCurrentUser]);

  /** Back to the user's own country. */
  const goHome = useCallback(() => {
    if (!u?.globalPassHomeRegion) return;
    updateCurrentUser({ region: u.globalPassHomeRegion, globalPassHomeRegion: null } as any);
  }, [u, updateCurrentUser]);

  return { exploring: isExploringPass(u), homeRegion: getHomeRegion(u), explore, goHome };
};

/**
 * Mount once at app level. When the pass ends (timer runs out, or the server says it
 * is no longer active) the app switches back to the home country by itself, on any page,
 * including after a reload.
 */
export const useGlobalPassAutoReturn = () => {
  const { user } = useAuth();
  const { goHome } = usePassView();
  const u = user as PassViewUser | null;
  const home = u?.globalPassHomeRegion;
  const expiresAt = u?.globalPassExpiresAt ?? 0;

  useEffect(() => {
    if (!home) return;
    const back = () => {
      goHome();
      toast(i18n.t("globalPass.endedToast", { region: regionLabel(i18n.t, getRegion(home)?.value ?? home) }));
    };
    const msLeft = expiresAt - Date.now();
    if (msLeft <= 0) {
      back();
      return;
    }
    const id = window.setTimeout(back, Math.min(msLeft, 2_147_000_000));
    return () => window.clearTimeout(id);
  }, [home, expiresAt, goHome]);
};

export const timeLeftParts = (ms: number): { hours: number; minutes: string } => ({
  hours: Math.floor(ms / 3_600_000),
  minutes: String(Math.floor((ms % 3_600_000) / 60_000)).padStart(2, "0"),
});
